import { Injectable } from "@angular/core";
import { Router } from "@angular/router";

import { BlockingUiSignalService } from "../blocking-ui-signal.service";
import { CoachMarkService } from "../coach-mark.service";
import { FeatureFlagService } from "../feature-flag.service";
import { InteractionTrackingService } from "../interaction-tracking.service";
import { NovaCompanionSettingsService } from "../nova-companion-settings.service";
import { OnboardingStepService } from "../onboarding-step.service";
import { PaymentsService } from "../payments.service";
import { LearnOrientationPrompt } from "./learn-orientation-lesson";
import { NovaChatLesson } from "./nova-chat-lesson";

export const N03B_LESSON_ID = "N03B";

/** Recorded with N03B's completed lesson state - N03B's CompletedAt *is* this milestone. */
export const NOVA_VOICE_CHAT_INTRODUCED = "nova_voice_chat_introduced";

/** Anchors workbook: the Voice Chat control, before the live conversation begins. */
export const N03B_ANCHOR = "nova.voice_chat";

/** Copy rows N03B.01-N03B.02, workbook v2.38 Copy sheet (verbatim, including ⌄ and the curly quotes). */
export const N03B_COPY = {
  heading: "Talk with Nova",
  body:
    "Voice Chat lets you have a live spoken conversation with Nova. Select ⌄ to minimise Nova while your " +
    "conversation continues. Select “Voice Chat Active” or × to close Nova and end the Voice Chat.",
};

/**
 * Onboarding lesson N03B "Talk with Nova" (FLIP-391; spec v2.36 §24.4, workbook v2.38): an
 * informational anchored prompt at Voice Chat, before the live conversation begins. Flow shared with
 * the other chat lessons - see NovaChatLesson.
 *
 * - Follows N03A automatically: the chat calls setReady(true) once N03A is done (completed, dismissed
 *   or already resolved on an earlier session), after N02's panel has been closed. It is not
 *   triggered by selecting Voice Chat, and never starts voice mode itself: dismissing it never
 *   activates the microphone or requests microphone access.
 * - Completes on the visible close, or on genuine Voice Chat use (`onTargetUsed("voice_chat")`).
 *   Standard informational dismissal completes permanently.
 */
@Injectable({ providedIn: "root" })
export class N03BTalkWithNovaService extends NovaChatLesson {
  protected readonly stepKey = N03B_LESSON_ID;
  protected readonly milestone = NOVA_VOICE_CHAT_INTRODUCED;

  constructor(
    router: Router,
    coachMarkService: CoachMarkService,
    blockingUiSignal: BlockingUiSignalService,
    featureFlagService: FeatureFlagService,
    paymentsService: PaymentsService,
    onboardingStepService: OnboardingStepService,
    interactionTracking: InteractionTrackingService,
    companionSettings: NovaCompanionSettingsService
  ) {
    super(
      router,
      coachMarkService,
      blockingUiSignal,
      featureFlagService,
      paymentsService,
      onboardingStepService,
      interactionTracking,
      companionSettings
    );
  }

  protected prompt(): LearnOrientationPrompt | null {
    return this.companionOn ? { lessonId: N03B_LESSON_ID, target: N03B_ANCHOR, ...N03B_COPY } : null;
  }
}
